import React, { useState } from 'react';
import Loader, { CommonLoader } from '../Common/Loader';
import Paper from '@material-ui/core/Paper';
import Avatar from '@material-ui/core/Avatar';
import { Row, Col, Input } from 'reactstrap';
import CommonTooltip from '../Common/CommonTooltip';
import moment from 'moment';
import { FormGroup, MenuItem, Select, Button } from '@material-ui/core';
import { formResMessage, convertModuleToName } from 'utils/commonFunc';
import PeopleIcon from '@material-ui/icons/People';
import EventIcon from '@material-ui/icons/Event';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import BadgeIcon from '@material-ui/icons/Person';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import AssignmentIcon from '@material-ui/icons/Assignment';
import VideocamIcon from '@material-ui/icons/Videocam';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';
import HistoryIcon from '@material-ui/icons/History';
import EditIcon from '@material-ui/icons/Edit';
import IconButton from '@material-ui/core/IconButton';
import colors from 'config/colors';

// Only manually-added announcements can be edited - everything else in the feed is a
// system-generated audit trail of real record changes and must stay immutable.
const EDITABLE_MODULE = 'ANNOUNC_MODULE';

// Icon + color per activity module, so the feed reads at a glance instead of being a
// flat wall of text. Falls back to a neutral "history" badge for anything unmapped.
const MODULE_STYLE = {
  STUDENT_MODULE: { icon: PeopleIcon, color: '#397af8' },
  BATCH_MODULE: { icon: EventIcon, color: colors.accent },
  COURSE_MODULE: { icon: MenuBookIcon, color: '#00897b' },
  EMPLOYEE_MODULE: { icon: BadgeIcon, color: '#f9a825' },
  CIRCULAR_MODULE: { icon: NotificationsActiveIcon, color: '#e53935' },
  ASSIGNMENT_MODULE: { icon: AssignmentIcon, color: '#5e35b1' },
  ONLINE_CLASS_MODULE: { icon: VideocamIcon, color: '#43a047' },
  EXAM_MODULE: { icon: AssignmentTurnedInIcon, color: '#fb8c00' },
  EXTRA_CURRICULAR_MODULE: { icon: EmojiEventsIcon, color: '#d81b60' },
};

// Modules without an explicit icon/color above (there are ~25+ of them) still need to
// read clearly as a tag, not fall back to a flat gray. Hash the name to a deterministic
// color from this palette so every module gets a distinct, consistent, high-contrast tag.
const FALLBACK_COLOR_PALETTE = [
  '#397af8',
  '#00897b',
  '#f9a825',
  '#e53935',
  '#5e35b1',
  '#43a047',
  '#fb8c00',
  '#d81b60',
  '#3949ab',
  '#00acc1',
  '#7cb342',
  '#8e24aa',
  '#546e7a',
  '#6d4c41',
];

const hashStringToIndex = (str, mod) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return hash % mod;
};

const getModuleStyle = moduleName => {
  if (MODULE_STYLE[moduleName]) return MODULE_STYLE[moduleName];
  if (!moduleName) return { icon: HistoryIcon, color: '#8b95a1' };
  return {
    icon: HistoryIcon,
    color: FALLBACK_COLOR_PALETTE[hashStringToIndex(moduleName, FALLBACK_COLOR_PALETTE.length)],
  };
};

const ActivityCard = ({ activity: el, updateActivity, updateSuccess, showWarning }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(el?.auditDescription || '');
  const [saving, setSaving] = useState(false);
  const { icon: ModuleIcon, color } = getModuleStyle(el?.moduleName);
  const isEditable = updateActivity && el?.moduleName === EDITABLE_MODULE;

  const startEdit = () => {
    setValue(el?.auditDescription || '');
    setEditing(true);
  };

  const cancelEdit = () => setEditing(false);

  const saveEdit = () => {
    if (!value?.trim()) return;
    setSaving(true);
    updateActivity(el.auditLogId, value)
      .then(() => {
        setSaving(false);
        setEditing(false);
        updateSuccess?.();
      })
      .catch(err => {
        setSaving(false);
        showWarning(formResMessage(err));
      });
  };

  return (
    <Paper variant="outlined" style={{ padding: 12, marginBottom: 8, borderRadius: 8 }}>
      <Row style={{ margin: 0 }}>
        <Avatar style={{ backgroundColor: `${color}1a`, color, marginRight: 12, width: 36, height: 36 }}>
          <ModuleIcon fontSize="small" />
        </Avatar>
        <Col style={{ padding: 0 }}>
          {editing ? (
            <div>
              <Input
                type="textarea"
                value={value}
                onChange={e => setValue(e.target.value)}
                maxLength="5000"
                autoFocus
                style={{ padding: '8px 12px' }}
              />
              <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={saving}
                  onClick={saveEdit}
                  style={{ backgroundColor: colors.accent, color: '#fff', fontWeight: 'bold' }}>
                  {saving ? <Loader size={20} /> : 'Save'}
                </Button>
                <Button
                  size="small"
                  disabled={saving}
                  onClick={cancelEdit}
                  style={{ color: colors.accent, fontWeight: 'bold' }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ fontSize: 14, flex: 1 }}>
                  {el?.auditDescription}{' '}
                  {el?.changesFor && (
                    <>
                      for <span style={{ color: '#6b778a', fontWeight: 'bold' }}>{el?.changesFor}</span>
                    </>
                  )}
                </div>
                {isEditable && (
                  <IconButton
                    size="small"
                    onClick={startEdit}
                    aria-label="Edit activity"
                    style={{ backgroundColor: `${colors.accent}1a`, marginLeft: 10, padding: 6, flexShrink: 0 }}>
                    <EditIcon style={{ fontSize: 16, color: colors.accent }} />
                  </IconButton>
                )}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 6,
                  marginTop: 6,
                  color: '#8b95a1',
                  fontSize: 12,
                }}>
                {el?.moduleName && (
                  <span
                    style={{
                      backgroundColor: `${color}1a`,
                      color,
                      border: `1px solid ${color}`,
                      fontWeight: 'bold',
                      fontSize: 11,
                      padding: '1px 8px',
                      borderRadius: 4,
                    }}>
                    {convertModuleToName(el.moduleName)}
                  </span>
                )}
                <span>
                  Updated by{' '}
                  <CommonTooltip content={el?.fromUserName}>
                    <span>{el?.fromUserFullName}</span>
                  </CommonTooltip>
                  {' · '}
                  <CommonTooltip content={moment(el.creationDateTime).format('DD MMM, YYYY hh:mm A')}>
                    <span>{moment(el.creationDateTime).fromNow()}</span>
                  </CommonTooltip>
                </span>
              </div>
            </>
          )}
        </Col>
      </Row>
    </Paper>
  );
};

const ActivityList = ({
  recentActivitiesFetching,
  activities,
  limit,
  handleLimitChange,
  showLimitAlways = false,
  sendVoice,
  updateActivity,
  updateSuccess,
  showWarning,
}) => {
  const [auditDescription, setAudit] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = e => {
    e.preventDefault();
    if (auditDescription) {
      setLoading(true);
      sendVoice({ auditDescription })
        .then(() => {
          setLoading(false);
          setAudit('');
          updateSuccess?.();
        })
        .catch(err => {
          setLoading(false);
          showWarning(formResMessage(err));
        });
    }
  };

  const noDataDiv = (
    <div style={{ textAlign: 'center', paddingTop: 40, paddingBottom: 40, color: '#8b95a1' }}>
      <HistoryIcon style={{ fontSize: 36, opacity: 0.4 }} />
      <div style={{ marginTop: 8 }}>No activities found</div>
    </div>
  );

  return (
    <div>
      {recentActivitiesFetching ? (
        <div style={{ textAlign: 'center' }}>
          <CommonLoader />
        </div>
      ) : (
        <>
          {(showLimitAlways || activities?.length > 0) && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 6,
                marginBottom: 8,
              }}>
              <span style={{ color: '#8b95a1', fontSize: 12 }}>Show</span>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={limit}
                style={{ minWidth: 60, fontSize: 13 }}
                onChange={handleLimitChange}>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={200}>200</MenuItem>
                <MenuItem value={500}>500</MenuItem>
              </Select>
            </div>
          )}
          {sendVoice && (
            <form onSubmit={handleSubmit}>
              <Paper
                variant="outlined"
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', marginBottom: 12 }}>
                <Input
                  style={{ border: 'none', boxShadow: 'none', flex: 1 }}
                  type="text"
                  name="auditDescription"
                  onChange={e => setAudit(e.target.value)}
                  value={auditDescription}
                  placeholder="Announce something to everyone..."
                />
                {auditDescription && (
                  <Button size="small" type="submit" color="secondary" variant="contained" disabled={loading}>
                    {loading ? <Loader size={20} /> : 'Add'}
                  </Button>
                )}
              </Paper>
            </form>
          )}
          {activities?.length > 0
            ? activities?.map((el, k) => (
                <ActivityCard
                  key={el?.auditLogId ?? k}
                  activity={el}
                  updateActivity={updateActivity}
                  updateSuccess={updateSuccess}
                  showWarning={showWarning}
                />
              ))
            : noDataDiv}
        </>
      )}
    </div>
  );
};

export default ActivityList;
